import React, { useState, useCallback, useEffect, useMemo } from "react";
import {
  Box,
  Button,
  Flex,
  Input,
  Text,
  useColorModeValue,
} from "@chakra-ui/react";
import Card from "components/card/Card";
import TermsTable from "./components/TermsTable";
import Pagination from "components/pagination";
import CreateTermsModal from "components/modals/CreateTermsModal";
import TermsDetails from "./components/TermsDetails";
import { useToastNotification } from 'utils/toast-utils';
import { useDispatch, useSelector } from 'react-redux';
import { fetchTerms, createTerm, deleteTerm } from '../../../features/terms/actions';

const TermsManagement = () => {
  const dispatch = useDispatch();
  const { showToast } = useToastNotification();
  const { terms, loading } = useSelector((state) => state.terms);
  const textColor = useColorModeValue("secondaryGray.900", "white");
  const [searchTerm, setSearchTerm] = useState("");
  const bgInput = useColorModeValue("transparent", "navy.900");
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [selectedTerm, setSelectedTerm] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);

  useEffect(() => {
    dispatch(fetchTerms({ page: 1, limit: 0 }));
  }, [dispatch]);

  // Add filtered terms based on search
  const filteredTerms = useMemo(() => {
    if (!searchTerm) return terms;
    return terms.filter(term => 
      term.title?.toLowerCase().includes(searchTerm.toLowerCase()));
  }, [terms, searchTerm]);

  // Add pagination calculations
  const totalItems = filteredTerms.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const currentPageData = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredTerms.slice(startIndex, endIndex);
  }, [filteredTerms, currentPage, itemsPerPage]);

  const handlePageChange = useCallback((data, page, limit) => {
    setCurrentPage(page);
    setItemsPerPage(limit);
  }, []);

  const handleCreateTerms = async (termsData) => {
    try {
      await dispatch(createTerm(termsData)).unwrap();
      showToast('Success', 'Terms created successfully', 'success');
      setIsCreateModalOpen(false);
      dispatch(fetchTerms({ page: 1, limit: 0 }));
    } catch (error) {
      showToast(
        'Error',
        error.message || 'Failed to create terms',
        'error'
      );
      throw error;
    }
  };

  const handleEdit = (term) => {
    setSelectedTerm(term);
    setIsEditMode(true);
    setShowDetails(true);
  };

  const handleDeleteTerm = async (termId) => {
    try {
      await dispatch(deleteTerm(termId)).unwrap();
      showToast(
        'Success',
        'Term deleted successfully',
        'success'
      );
    } catch (error) {
      showToast(
        'Error',
        error.message || 'Failed to delete term',
        'error'
      );
    }
  };

  return (
    <Card borderRadius="0" p="0">
      {showDetails ? (
        <TermsDetails
          termsData={selectedTerm}
          onBack={() => {
            setShowDetails(false);
            setSelectedTerm(null);
            setIsEditMode(false);
          }}
          isEditMode={isEditMode}
        />
      ) : (
        <Flex direction="column">
          <Flex justify="space-between" align="center" mb={4}>
            <Text color={textColor} fontSize="30px" fontWeight="700" lineHeight="40px">
              Terms
            </Text>
            <Flex gap={5}>
              <Input
                placeholder="Search"
                borderRadius="4px"
                size="md"
                width="218px"
                height="40px"
                bg={bgInput}
                _placeholder={{ color: "gray.400" }}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <Button
                color='white'
                colorScheme="purple"
                size="md"
                onClick={() => setIsCreateModalOpen(true)}
              >
                Create Terms
              </Button>
            </Flex>
          </Flex>
          <Card borderRadius="8px" boxShadow="0px 0px 12px 0px #0000001A">
            <TermsTable 
              tableData={currentPageData}
              onViewDetails={(term) => {
                setSelectedTerm(term);
                setShowDetails(true);
              }}
              onEdit={handleEdit}
              onDeleteTerm={handleDeleteTerm}
            />
            <Flex justify="space-between" align="center">
              <Pagination
                data={filteredTerms}
                onPageChange={handlePageChange}
                initialItemsPerPage={itemsPerPage}
                currentPage={currentPage}
                totalItems={totalItems}
                totalPages={totalPages}
                hasMoreData={currentPage < totalPages}
              />
            </Flex>
          </Card>
          <CreateTermsModal
            isOpen={isCreateModalOpen}
            onClose={() => setIsCreateModalOpen(false)}
            onSubmit={handleCreateTerms}
          />
        </Flex>
      )}
    </Card>
  );
};

export default TermsManagement;
